import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Copy, Hammer, User as UserIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/ApiService';
import { htmlToMarkdown } from '../lib/markdown-utils';
import type { OnePager } from '../types';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

interface ProfileUser {
  id: string;
  email: string;
  username: string;
  fullName?: string;
}

export const ProfilePage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const [profileUser, setProfileUser] = useState<ProfileUser | null>(null);
  const [onePagers, setOnePagers] = useState<OnePager[]>([]);
  const [metrics, setMetrics] = useState({ totalClones: 0, totalBuilds: 0, totalOnePagers: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // If no username in params, use current user's username
  const effectiveUsername = username || currentUser?.username;

  useEffect(() => {
    if (effectiveUsername) {
      loadUserProfile();
    }
  }, [effectiveUsername]);

  const loadUserProfile = async () => {
    if (!effectiveUsername) return;

    try {
      setLoading(true);
      setError(null);
      const [user, onePagersData, userMetrics] = await Promise.all([
        apiService.getUserByUsername(effectiveUsername),
        apiService.getOnePagersByUsername(effectiveUsername),
        apiService.getUserMetrics(effectiveUsername)
      ]);
      setProfileUser(user as ProfileUser);
      setOnePagers(onePagersData);
      setMetrics(userMetrics);
    } catch (error) {
      console.error('Failed to load user profile:', error);
      setError(error instanceof Error ? error.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const renderPreviewHtml = (onePager: OnePager) => {
    if (onePager.content && onePager.content.trim()) {
      return onePager.content;
    }
    return '<p class="text-gray-400 italic">No content available</p>';
  };

  const handleCreateFromTemplate = (onePager: OnePager) => {
    navigate('/create', { state: { templateOnePager: onePager } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-600">Loading profile...</div>
      </div>
    );
  }

  if (error || !profileUser) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-600 mb-4">{error || 'User not found'}</div>
          <Button onClick={() => navigate('/')} variant="outline">
            Go back to home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Profile Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div>
              {profileUser.fullName && (
                <h1 className="text-2xl font-bold text-gray-900 mb-1">{profileUser.fullName}</h1>
              )}
              <p className="text-lg text-gray-600 mb-2">@{profileUser.username}</p>
              
              {/* Metrics under username */}
              <div className="flex gap-6 text-sm">
                <div>
                  <span className="font-semibold text-gray-900">{metrics.totalClones}</span>
                  <span className="text-gray-600 ml-1">Clones</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-900">{metrics.totalBuilds}</span>
                  <span className="text-gray-600 ml-1">Builds</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* One-pagers Grid */}
        {onePagers.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {onePagers.map((onePager) => (
              <Card
                key={onePager.id}
                className="border-gray-200 w-full sm:w-full md:max-w-96 hover:border-gray-300 hover:shadow-md transition-all cursor-pointer group bg-white shadow-sm rounded-sm"
                onClick={() => navigate(`/o/${onePager.id}`)}
              >
                {/* Portrait mini preview */}
                <div className="w-full h-96 p-5 overflow-hidden bg-gray-50 border-b border-gray-200 relative">
                  <div className="absolute inset-0 bg-gradient-to-b from-white to-gray-50 opacity-30"></div>
                  <div className="relative z-10 prose prose-sm max-w-none text-gray-900 scale-50 origin-top-left leading-7 [&_h1]:text-4xl [&_h1]:font-bold [&_h1]:leading-tight [&_h1]:my-8 [&_h1]:text-gray-900 [&_h2]:text-3xl [&_h2]:font-semibold [&_h2]:leading-snug [&_h2]:mt-6 [&_h2]:mb-3 [&_h2]:text-gray-900 [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:leading-normal [&_h3]:mt-5 [&_h3]:mb-2 [&_h3]:text-gray-900 [&_p]:my-3 [&_p]:leading-7 [&_ul]:my-3 [&_ul]:pl-6 [&_ul]:list-disc [&_ol]:my-3 [&_ol]:pl-6 [&_ol]:list-decimal [&_li]:my-1 [&_li]:leading-relaxed [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:my-4 [&_blockquote]:italic [&_blockquote]:text-gray-600 [&_pre]:bg-gray-100 [&_pre]:rounded-lg [&_pre]:p-4 [&_pre]:my-4 [&_pre]:overflow-x-auto [&_pre]:font-mono [&_pre]:text-sm [&_pre]:leading-relaxed [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:font-mono [&_code]:text-sm" style={{ height: '200%', overflow: 'hidden', width: '200%' }}
                    dangerouslySetInnerHTML={{ __html: renderPreviewHtml(onePager) }}
                  />
                  <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-gray-200 to-transparent opacity-30"></div>
                </div>

                {/* Description, metrics, and buttons */}
                <div className="flex flex-col p-4 bg-white h-36">
                  {/* Metrics with icons above description */}
                  <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                    <div className="flex items-center gap-1">
                      <Copy className="w-3 h-3" />
                      <span>{onePager.clone_count || 0}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Hammer className="w-3 h-3" />
                      <span>{onePager.build_count || 0}</span>
                    </div>
                  </div>
                  <div className="mb-2 h-8">
                    {onePager.description ? (
                      <p className="text-xs text-gray-600 leading-relaxed line-clamp-2">
                        {onePager.description}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-400 italic leading-relaxed line-clamp-2">
                        No description available
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 mt-auto">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async (e) => {
                        e.stopPropagation();
                        // Track clone
                        try {
                          await apiService.incrementCloneCount(parseInt(onePager.id));
                        } catch (error) {
                          console.error('Failed to track clone:', error);
                        }
                        handleCreateFromTemplate(onePager);
                      }}
                      className="flex-1 h-8 text-xs font-normal border-gray-200 hover:border-gray-300 rounded-sm transform hover:scale-105 transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      <Copy className="w-3 h-3 mr-1" /> Clone
                    </Button>
                    <Button
                      size="sm"
                      className="relative flex-1 h-8 text-xs font-normal rounded-sm text-gray-900 bg-white transform hover:scale-105 transition-all duration-200 shadow-sm hover:shadow-md before:absolute before:inset-0 before:rounded-sm before:p-[2px] before:bg-gradient-to-r before:from-purple-500 before:via-blue-500 before:to-cyan-500 hover:before:from-purple-600 hover:before:via-blue-600 hover:before:to-cyan-600 before:-z-10 after:absolute after:inset-[2px] after:rounded-[calc(0.125rem-2px)] after:bg-white after:-z-10"
                      onClick={async (e) => {
                        e.stopPropagation();
                        // Track build
                        try {
                          await apiService.incrementBuildCount(parseInt(onePager.id));
                        } catch (error) {
                          console.error('Failed to track build:', error);
                        }
                        const markdownContent = htmlToMarkdown(onePager.content || '');
                        const fullPrompt = `${onePager.title || ''}\n\n${onePager.description || ''}\n\n${markdownContent}`;
                        const lovableUrl = `https://lovable.dev/?autosubmit=true#prompt=${encodeURIComponent(fullPrompt)}`;
                        window.open(lovableUrl, '_blank');
                      }}
                    >
                      <Hammer className="w-3 h-3 mr-1" />
                      Build
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-500">You haven't created any one-pagers yet.</div>
            <Button onClick={() => navigate('/create')} variant="outline" className="mt-4 border-gray-200 hover:border-gray-300">
              Create your first one-pager
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
